import { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

const EMPTY = {
  company_name:'', address:'', phone:'', associated_email:'',
  bank_name:'', bank_account:'', bank_iban:'', bank_swift:'',
  email_provider:'', email_username:'',
  shopify_store_url:'', funnelish_account:'',
  klaviyo_id:'', klaviyo_password:'',
  wetracked_id:'', wetracked_password:'',
  pixel_id:'', ad_account_id:'', notes:''
}

const FIELD_GROUPS = [
  {
    group: 'Company', icon: '🏢', fields: [
      { key:'company_name',    label:'Company Name' },
      { key:'address',         label:'Address' },
      { key:'phone',           label:'Phone' },
      { key:'associated_email',label:'Associated Email' },
    ]
  },
  {
    group: 'Banking', icon: '🏦', fields: [
      { key:'bank_name',    label:'Bank Name' },
      { key:'bank_account', label:'Account No.', secret:true },
      { key:'bank_iban',    label:'IBAN', secret:true },
      { key:'bank_swift',   label:'SWIFT/BIC' },
    ]
  },
  {
    group: 'Email', icon: '✉️', fields: [
      { key:'email_provider',  label:'Email Provider' },
      { key:'email_username',  label:'Email Username' },
    ]
  },
  {
    group: 'Shopify', icon: '🛒', fields: [
      { key:'shopify_store_url', label:'Store URL' },
    ]
  },
  {
    group: 'Funnelish', icon: '⚡', fields: [
      { key:'funnelish_account', label:'Account/Email' },
    ]
  },
  {
    group: 'Klaviyo', icon: '📧', fields: [
      { key:'klaviyo_id',       label:'Klaviyo ID' },
      { key:'klaviyo_password', label:'Password', secret:true },
    ]
  },
  {
    group: 'Wetracked', icon: '📊', fields: [
      { key:'wetracked_id',       label:'Wetracked ID' },
      { key:'wetracked_password', label:'Password', secret:true },
    ]
  },
  {
    group: 'Ads', icon: '📣', fields: [
      { key:'pixel_id',      label:'Pixel ID' },
      { key:'ad_account_id', label:'Ad Account ID' },
    ]
  },
  {
    group: 'Notes', icon: '📝', fields: [
      { key:'notes', label:'Notes' },
    ]
  },
]

// Flatten all fields for column headers
const ALL_FIELDS = FIELD_GROUPS.flatMap(g => g.fields)

function SecretCell({ value, onChange }) {
  const [show, setShow] = useState(false)
  return (
    <div style={{display:'flex',alignItems:'center',gap:4,minWidth:140}}>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        style={{flex:1,background:'transparent',border:'none',outline:'none',
          color:'var(--text)',fontSize:12,fontFamily:'DM Mono,monospace',width:'100%'}}
      />
      <button onClick={()=>setShow(s=>!s)} style={{
        background:'none',border:'none',cursor:'pointer',
        color:'var(--text3)',fontSize:11,flexShrink:0,padding:'0 2px'
      }}>{show?'🙈':'👁'}</button>
    </div>
  )
}

function EditCell({ value, onChange, field }) {
  if (field.secret) return <SecretCell value={value} onChange={onChange} />
  return (
    <input
      value={value}
      onChange={onChange}
      style={{width:'100%',background:'transparent',border:'none',outline:'none',
        color:'var(--text)',fontSize:12,fontFamily:'DM Mono,monospace',minWidth:120}}
    />
  )
}

export default function Company() {
  const [markets, setMarkets] = useState([])
  const [data, setData] = useState({}) // { market: {...fields} }
  const [saving, setSaving] = useState({})
  const [loading, setLoading] = useState(true)
  const [newMarket, setNewMarket] = useState('')
  const [addingMarket, setAddingMarket] = useState(false)
  const [showAddMarket, setShowAddMarket] = useState(false)
  const [activeGroup, setActiveGroup] = useState('Company')
  const [showSecrets, setShowSecrets] = useState({}) // per market+field

  useEffect(() => { loadAll() }, [])

  async function loadAll() {
    setLoading(true)
    try {
      const token = localStorage.getItem('adops_token')
      const r = await fetch('/api/company', { headers: { Authorization: `Bearer ${token}` } })
      const rows = await r.json()
      const mapped = {}
      const mks = []
      rows.forEach(row => { mapped[row.market] = row; mks.push(row.market) })
      setData(mapped)
      setMarkets(mks)
    } catch (err) { toast.error('Failed to load: ' + err.message) }
    setLoading(false)
  }

  function setField(market, key, value) {
    setData(prev => ({
      ...prev,
      [market]: { ...(prev[market] || { ...EMPTY, market }), [key]: value }
    }))
  }

  async function saveMarket(market) {
    setSaving(prev => ({ ...prev, [market]: true }))
    try {
      const token = localStorage.getItem('adops_token')
      const form = data[market] || { ...EMPTY, market }
      const r = await fetch(`/api/company/${market}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      })
      const saved = await r.json()
      if (saved.error) throw new Error(saved.error)
      setData(prev => ({ ...prev, [market]: saved }))
      toast.success(`${market} saved!`)
    } catch (err) { toast.error(err.message) }
    setSaving(prev => ({ ...prev, [market]: false }))
  }

  async function addMarket() {
    const m = newMarket.trim().toUpperCase()
    if (!m) { toast.error('Enter a market code'); return }
    if (markets.includes(m)) { toast.error('Market already exists'); return }
    setAddingMarket(true)
    try {
      const token = localStorage.getItem('adops_token')
      await fetch(`/api/company/${m}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...EMPTY, market: m })
      })
      setMarkets(prev => [...prev, m])
      setData(prev => ({ ...prev, [m]: { ...EMPTY, market: m } }))
      setNewMarket('')
      setShowAddMarket(false)
      toast.success(`Market ${m} added!`)
    } catch (err) { toast.error(err.message) }
    setAddingMarket(false)
  }

  const currentGroup = FIELD_GROUPS.find(g => g.group === activeGroup)

  if (loading) return (
    <div style={{padding:40,color:'var(--text2)',fontFamily:'DM Mono,monospace',fontSize:13}}>
      Loading company info...
    </div>
  )

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'}}>

      {/* Toolbar */}
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">🏢 Company Info</span>
          <span className="toolbar-sub">{markets.length} markets</span>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {showAddMarket && (
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <input
                value={newMarket}
                onChange={e=>setNewMarket(e.target.value.toUpperCase())}
                onKeyDown={e=>e.key==='Enter'&&addMarket()}
                placeholder="e.g. IT, PL, BE"
                maxLength={10}
                style={{width:120,padding:'6px 10px',background:'var(--bg3)',border:'1px solid var(--border2)',
                  borderRadius:7,color:'var(--text)',fontSize:12,fontFamily:'DM Mono,monospace',outline:'none'}}
                autoFocus
              />
              <button className="btn btn-primary btn-sm" onClick={addMarket} disabled={addingMarket}>
                {addingMarket ? '...' : 'Add'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowAddMarket(false)}>Cancel</button>
            </div>
          )}
          {!showAddMarket && (
            <button className="btn btn-primary" onClick={()=>setShowAddMarket(true)}>+ Add Market</button>
          )}
        </div>
      </div>

      {/* Group tabs */}
      <div style={{display:'flex',gap:2,padding:'10px 20px 0',background:'var(--bg2)',
        borderBottom:'1px solid var(--border)',flexShrink:0,overflowX:'auto',scrollbarWidth:'none'}}>
        {FIELD_GROUPS.map(g => (
          <button key={g.group} onClick={()=>setActiveGroup(g.group)} style={{
            padding:'7px 14px',border:'none',borderRadius:'8px 8px 0 0',cursor:'pointer',
            fontSize:12,fontWeight:600,transition:'all 0.15s',whiteSpace:'nowrap',
            background: activeGroup===g.group ? 'var(--bg)' : 'transparent',
            color: activeGroup===g.group ? 'var(--text)' : 'var(--text3)',
            borderBottom: activeGroup===g.group ? '2px solid var(--accent)' : '2px solid transparent',
          }}>
            {g.icon} {g.group}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{flex:1,overflow:'auto'}}>
        {markets.length === 0 ? (
          <div style={{padding:60,textAlign:'center',color:'var(--text3)'}}>
            <div style={{fontSize:32,marginBottom:10}}>🏢</div>
            <div style={{fontSize:14,fontWeight:600}}>No markets yet</div>
            <div style={{fontSize:12,marginTop:4}}>Click "+ Add Market" to get started</div>
          </div>
        ) : (
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr>
                <th style={{
                  background:'var(--bg3)',color:'var(--text2)',padding:'10px 16px',
                  textAlign:'left',fontSize:10,fontWeight:700,textTransform:'uppercase',
                  letterSpacing:'0.8px',fontFamily:'DM Mono,monospace',
                  border:'1px solid var(--border)',whiteSpace:'nowrap',
                  position:'sticky',left:0,zIndex:3,width:80
                }}>Market</th>
                {currentGroup?.fields.map(f => (
                  <th key={f.key} style={{
                    background:'var(--bg3)',color:'var(--text2)',padding:'10px 14px',
                    textAlign:'left',fontSize:10,fontWeight:700,textTransform:'uppercase',
                    letterSpacing:'0.8px',fontFamily:'DM Mono,monospace',
                    border:'1px solid var(--border)',whiteSpace:'nowrap',minWidth:160
                  }}>{f.label}</th>
                ))}
                <th style={{
                  background:'var(--bg3)',color:'var(--text2)',padding:'10px 14px',
                  textAlign:'center',fontSize:10,fontWeight:700,textTransform:'uppercase',
                  letterSpacing:'0.8px',fontFamily:'DM Mono,monospace',
                  border:'1px solid var(--border)',width:90,
                  position:'sticky',right:0,zIndex:3
                }}>Save</th>
              </tr>
            </thead>
            <tbody>
              {markets.map((market, idx) => {
                const row = data[market] || { ...EMPTY, market }
                return (
                  <tr key={market} style={{background: idx%2===0 ? 'var(--bg2)' : 'rgba(255,255,255,0.015)'}}>
                    {/* Market label */}
                    <td style={{
                      padding:'10px 16px',border:'1px solid var(--border)',
                      position:'sticky',left:0,zIndex:2,
                      background: idx%2===0 ? 'var(--bg2)' : 'var(--bg)',
                      whiteSpace:'nowrap'
                    }}>
                      <div style={{fontWeight:700,fontSize:12,color:'var(--accent)',
                        fontFamily:'DM Mono,monospace'}}>{market}</div>
                    </td>
                    {/* Field cells */}
                    {currentGroup?.fields.map(f => (
                      <td key={f.key} style={{
                        padding:'6px 12px',border:'1px solid var(--border)',
                        minWidth:160,verticalAlign:'middle'
                      }}>
                        <EditCell
                          value={row[f.key] || ''}
                          onChange={e => setField(market, f.key, e.target.value)}
                          field={f}
                        />
                      </td>
                    ))}
                    {/* Save button */}
                    <td style={{
                      padding:'6px 10px',border:'1px solid var(--border)',
                      textAlign:'center',position:'sticky',right:0,zIndex:2,
                      background: idx%2===0 ? 'var(--bg2)' : 'var(--bg)',
                    }}>
                      <button className="btn btn-primary btn-sm"
                        onClick={()=>saveMarket(market)}
                        disabled={saving[market]}
                        style={{whiteSpace:'nowrap',fontSize:11}}>
                        {saving[market] ? '...' : '💾 Save'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
